import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock, Image, Video, Mic, BookOpen, Sparkles,
  RefreshCw, ChevronDown, ChevronUp, Calendar,
  FileText, Quote, Flame
} from "lucide-react";
import { pageTransition, fadeUp } from "@/lib/animations";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { toast } from "sonner";

const typeConfig = {
  vault: {
    photo: { icon: Image, color: "text-sky-400", bg: "bg-sky-400/10", border: "border-sky-400/20", label: "Photo" },
    video: { icon: Video, color: "text-violet-400", bg: "bg-violet-400/10", border: "border-violet-400/20", label: "Video" },
    voice_note: { icon: Mic, color: "text-emerald-400", bg: "bg-emerald-400/10", border: "border-emerald-400/20", label: "Voice Note" },
    document: { icon: FileText, color: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/20", label: "Document" },
  },
  journal: {
    journal: { icon: BookOpen, color: "text-rose-400", bg: "bg-rose-400/10", border: "border-rose-400/20", label: "Journal" },
    prompt_response: { icon: Quote, color: "text-[#00FF88]", bg: "bg-[#00FF88]/10", border: "border-[#00FF88]/20", label: "Prompt Response" },
  },
  prompt: {
    reflection: { icon: Sparkles, color: "text-[#00FF88]", bg: "bg-[#00FF88]/10", border: "border-[#00FF88]/20", label: "Daily Reflection" },
  },
};

function getConfig(item) {
  return typeConfig[item.type]?.[item.subtype] || {
    icon: Clock, color: "text-slate-400", bg: "bg-slate-400/10", border: "border-slate-400/20", label: "Memory"
  };
}

function formatMonthKey(key) {
  if (!key || key === "unknown") return "Timeless";
  try {
    const [year, month] = key.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  } catch {
    return key;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return dateStr;
  }
}

function TimelineItem({ item, index, isLast }) {
  const config = getConfig(item);
  const Icon = config.icon;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const isImage = item.type === "vault" && item.content_type?.startsWith("image");

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="flex gap-4 md:gap-6 group"
      data-testid={`timeline-item-${item.id}`}
    >
      {/* Timeline spine */}
      <div className="flex flex-col items-center shrink-0 w-10">
        <div className={`w-10 h-10 rounded-xl ${config.bg} border ${config.border} flex items-center justify-center z-10 group-hover:scale-110 transition-transform duration-300`}>
          <Icon className={`w-4 h-4 ${config.color}`} strokeWidth={1.5} />
        </div>
        {!isLast && (
          <div className="w-px flex-1 bg-gradient-to-b from-white/10 to-transparent min-h-[40px]" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-8 min-w-0">
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 hover:border-white/10 transition-colors duration-300 overflow-hidden">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-[10px] font-semibold tracking-wider uppercase ${config.color}`}>
              {config.label}
            </span>
            <span className="text-[10px] text-slate-600">{formatDate(item.date)}</span>
            {item.mood && (
              <span className="text-[10px] bg-white/5 text-slate-400 px-2 py-0.5 rounded-full">{item.mood}</span>
            )}
          </div>

          <h4 className="text-sm font-medium text-white mb-1 truncate" style={{ fontFamily: "Manrope, sans-serif" }}>
            {item.title}
          </h4>

          {item.description && (
            <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">{item.description}</p>
          )}

          {/* Image preview for vault photos */}
          {isImage && item.file_id && (
            <div className="mt-3 rounded-xl overflow-hidden max-h-48">
              <img
                src={`${BACKEND_URL}/api/vault/files/${item.file_id}`}
                alt={item.title}
                className="w-full h-full object-cover rounded-xl"
                loading="lazy"
              />
            </div>
          )}

          {item.prompt_text && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-[#00FF88]/5 border border-[#00FF88]/10">
              <p className="text-[11px] text-[#00FF88]/80 italic">"{item.prompt_text}"</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function MonthChapter({ monthKey, narrative, items, startIndex }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="mb-8" data-testid={`timeline-month-${monthKey}`}>
      {/* Month header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-3 mb-6 group cursor-pointer w-full text-left"
        data-testid={`timeline-toggle-${monthKey}`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-[#00FF88]/10 border border-[#00FF88]/20 flex items-center justify-center shrink-0">
            <Calendar className="w-4 h-4 text-[#00FF88]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
              {formatMonthKey(monthKey)}
            </h3>
            {narrative && (
              <p className="text-xs text-slate-400 mt-0.5 line-clamp-1 italic">{narrative}</p>
            )}
          </div>
          <span className="text-xs text-slate-600 shrink-0">{items.length} memories</span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-600 shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-600 shrink-0" />
          )}
        </div>
      </button>

      {/* Chapter narrative */}
      <AnimatePresence>
        {expanded && narrative && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 ml-[52px] md:ml-[64px]"
          >
            <div className="glass-card rounded-xl p-4 border border-[#00FF88]/10">
              <p className="text-sm text-slate-300 leading-relaxed italic">{narrative}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Items */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {items.map((item, i) => (
              <TimelineItem
                key={item.id || `${monthKey}-${i}`}
                item={item}
                index={startIndex + i}
                isLast={i === items.length - 1}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function TimelinePage() {
  const { user } = useAuth();
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const loadTimeline = useCallback(async () => {
    try {
      const res = await api.get("/timeline");
      setTimeline(res.data);
    } catch (err) {
      console.error("Timeline load error:", err);
      toast.error("Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.post("/timeline/regenerate");
      setTimeline(res.data);
      toast.success("Timeline refreshed with new AI narrative!");
    } catch {
      toast.error("Failed to regenerate timeline");
    } finally {
      setRegenerating(false);
    }
  };

  // Group items by month
  const groupedByMonth = {};
  if (timeline?.items) {
    for (const item of timeline.items) {
      const dateStr = item.date || "";
      let monthKey = "unknown";
      try {
        if (dateStr.length >= 7) monthKey = dateStr.slice(0, 7);
      } catch { /* ignore */ }
      if (!groupedByMonth[monthKey]) groupedByMonth[monthKey] = [];
      groupedByMonth[monthKey].push(item);
    }
  }
  const sortedMonths = Object.keys(groupedByMonth).sort().reverse();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12" data-testid="timeline-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">Crafting your story...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-12 max-w-4xl mx-auto" data-testid="timeline-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between mb-10 gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Memory Timeline</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>
            Your Story
          </h1>
          <p className="text-sm text-slate-400 mt-2 max-w-xl">
            Every memory, journal entry, and reflection — woven into the story of your life by AI.
          </p>
        </div>
        <Button
          onClick={handleRegenerate}
          disabled={regenerating}
          data-testid="timeline-regenerate-btn"
          className="bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-full px-5 font-medium shrink-0"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${regenerating ? "animate-spin" : ""}`} />
          {regenerating ? "Regenerating..." : "Refresh Story"}
        </Button>
      </div>

      {/* Empty State */}
      {(!timeline?.items || timeline.items.length === 0) && (
        <div className="text-center py-24">
          <div className="w-16 h-16 rounded-2xl bg-[#00FF88]/10 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-[#00FF88]" />
          </div>
          <p className="text-lg text-white mb-2" style={{ fontFamily: "Manrope" }}>Your story starts here</p>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Upload photos, write journal entries, and respond to daily prompts to build your memory timeline.
          </p>
        </div>
      )}

      {/* AI Narrative Overview */}
      {timeline?.narrative && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="relative rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#00FF88]/5 via-[#0A0A0A] to-[#0A0A0A]" />
            <div className="relative p-8 md:p-10 border border-[#00FF88]/15 rounded-2xl">
              <div className="flex items-center gap-2 mb-4">
                <Flame className="w-4 h-4 text-[#00FF88]" />
                <span className="text-xs tracking-[0.2em] uppercase text-[#00FF88] font-medium">Your Narrative</span>
              </div>
              <p
                className="text-base md:text-lg text-slate-200 leading-relaxed italic"
                style={{ fontFamily: "Manrope, sans-serif" }}
                data-testid="timeline-narrative"
              >
                "{timeline.narrative}"
              </p>
              <div className="flex items-center gap-6 mt-6 pt-4 border-t border-white/[0.06]">
                <div className="text-center">
                  <p className="text-xl font-bold text-white" style={{ fontFamily: "Manrope" }}>{timeline.total_items}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Memories</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-white" style={{ fontFamily: "Manrope" }}>{sortedMonths.length}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Chapters</p>
                </div>
                {timeline.generated_at && (
                  <div className="ml-auto">
                    <p className="text-[10px] text-slate-600">
                      Generated {new Date(timeline.generated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Timeline Chapters */}
      {sortedMonths.length > 0 && (
        <div data-testid="timeline-chapters">
          {sortedMonths.map((monthKey, mIdx) => {
            const prevCount = sortedMonths.slice(0, mIdx).reduce((acc, k) => acc + groupedByMonth[k].length, 0);
            return (
              <MonthChapter
                key={monthKey}
                monthKey={monthKey}
                narrative={timeline?.chapter_narratives?.[monthKey] || ""}
                items={groupedByMonth[monthKey]}
                startIndex={prevCount}
              />
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
